import produce from 'immer'
import set from 'lodash/fp/set'
import intersection from 'lodash/intersection'
import isEmpty from 'lodash/isEmpty'

import { BannerRequestBody } from 'api/Banner/types'
import {
    CerebroApiOptions,
    CerebroPaginatedResponse,
    CerebroResourceResponse,
} from 'api/Cerebro/types'
import {
    AMS_TIME_AGGREGATE_FIELDS,
    CATEGORY_INSIGHTS_TIME_AGGREGATE_FIELDS,
    CHANGELOG_TIME_AGGREGATE_FIELDS,
    DATA_SOURCES,
    SOV_TIME_AGGREGATE_FIELDS,
} from 'configuration/widgets'
import { Y_BOTTOM_OF_PAGE } from 'const/dashboards'
import { AGGREGATION, DATES } from 'const/filters'
import { CATEGORY_CHART, TABLE, TIME_SERIES } from 'const/widgets'
import { deserializeDatesFilter } from 'helpers/filters'
import { transformToString } from 'helpers/widgets'
import {
    defaultDashboardTabState,
    defaultWidgetState,
} from 'reducers/ui/defaults'
import {
    DashboardTab,
    Field,
    FiltersState,
    ModifiedWidget,
    Widget,
} from 'types'
import { Report } from 'types/resources'

function getGroupByDependencyIds(
    dataSource: Widget['data_source'],
    dimensionId: string
): string[] {
    const dependencies =
        DATA_SOURCES[dataSource]?.groupByDependencies?.[dimensionId] ?? []
    return dependencies
        .map((dependency) => dependency?.id)
        .filter((id): id is string => Boolean(id) && id !== dimensionId)
}

export function formatAutoFilters(widget: Widget): any {
    const widgetGroupBy = transformToString(widget.group_by)
    const widgetMetrics = transformToString(widget.metrics)
    const params: any = {}

    if (widget.auto_filter) {
        params.auto_filter = widget.auto_filter
        params.auto_filter_limit = widget.auto_filter_limit
        params.auto_filter_direction = widget.auto_filter_direction
        // The auto filter (top_n) must group by the same dimensions as the
        // outer query. getWidgetGroupBys expands the selected group by with its
        // groupByDependencies, so mirror that here — otherwise the top_n group
        // by (e.g. `product_info`) won't match the outer group by
        // (`product_info,product_id`) and the ranking is computed at the wrong
        // grain.
        const primaryDimension =
            widget.type === TIME_SERIES ? widgetGroupBy[1] : widgetGroupBy[0]
        params.auto_filter_dimension = primaryDimension
            ? [
                  primaryDimension,
                  ...getGroupByDependencyIds(
                      widget.data_source,
                      primaryDimension
                  ),
              ].join(',')
            : primaryDimension
        params.auto_filter_metric = widgetMetrics[0]
        params.auto_filter_include_others =
            widget.auto_filter_include_others ?? false

        if (widgetMetrics.length === 1) {
            params[`${widgetMetrics[0]}__isnotnull`] = true // filter out nulls
        }

        // For category charts we don't want the limit to override auto_filter_limit
        if (widget.type === CATEGORY_CHART) {
            params.limit = undefined
        }
    }

    return params
}

export function formatGoals(widget: Widget): {
    [metric: string]: string | number
} {
    if (widget.goals) {
        return widget.goals.reduce((agg: any, goal) => {
            agg[goal.metric] = goal.value
            return agg
        }, {})
    }
    return {}
}

export const isGroupedByDate = (groupBy: string[]): boolean =>
    intersection(groupBy, SOV_TIME_AGGREGATE_FIELDS).length > 0 ||
    intersection(groupBy, AMS_TIME_AGGREGATE_FIELDS).length > 0 ||
    intersection(groupBy, CHANGELOG_TIME_AGGREGATE_FIELDS).length > 0 ||
    intersection(groupBy, CATEGORY_INSIGHTS_TIME_AGGREGATE_FIELDS).length > 0

export function formatWidgetGroupBy(
    widget: Widget | ModifiedWidget,
    pageFilters: FiltersState,
    format = 'string'
): any {
    const rawGroupBys = widget.group_by
    const stringGroupBys = transformToString(rawGroupBys)
    const stringMetrics = transformToString(widget.metrics)

    const params: any = {}
    if (
        widget.type === TIME_SERIES &&
        !isEmpty(pageFilters[AGGREGATION]) &&
        !widget.doNotOverrideAggregation
    ) {
        const key = DATA_SOURCES[widget.data_source]?.datePrefix
        // override the first group_by element with page-level aggregate filter value
        params.group_by = set(
            ['0'],
            `${key}_${pageFilters[AGGREGATION]}`
        )(stringGroupBys)
        params.ordering = `${key}_${pageFilters[AGGREGATION]}`
    } else if (stringGroupBys.length > 0) {
        // just let them be as they come, so we respect formatting settings like the width
        params.group_by = format === 'string' ? stringGroupBys : rawGroupBys
    }

    // If grouped by a date field add a metric filter to filter out nulls
    if (isGroupedByDate(stringGroupBys) && stringMetrics.length === 1) {
        params[`${stringMetrics[0]}__isnotnull`] = true
    }

    // String format used for url query params, array format used for rendering chart
    if (params.group_by && format === 'string') {
        params.group_by = params.group_by.join()
    }

    return params
}

export function getWidgetApiFunc(
    widget: Pick<
        Widget | ModifiedWidget,
        'filters' | 'data_source' | 'group_by'
    >
): (
    params: BannerRequestBody,
    options?: CerebroApiOptions
) => Promise<CerebroPaginatedResponse<any> | null> {
    const { data_source } = widget
    const widgetConfig = DATA_SOURCES[data_source]
    return widgetConfig?.getApiFunc(widget) ?? (() => Promise.resolve(null))
}

export function getWidgetExportApiFunc(
    widget: Widget
): (
    params: any,
    options?: object
) => Promise<CerebroResourceResponse<Report> | null> {
    const { data_source } = widget
    const widgetConfig = DATA_SOURCES[data_source]
    return (
        widgetConfig?.getExportApiFunc?.(widget) ??
        (() => Promise.resolve(null))
    )
}

export function getWidgetApiParams(
    widget: Widget
): Record<string, any> | undefined {
    const { data_source } = widget
    const widgetConfig = DATA_SOURCES[data_source]
    return widgetConfig?.getApiParams?.(widget) ?? {}
}

// Per-metric flags derived from the glossary, used to skip variants the
// backend won't serve. Optional — when undefined, getWidgetMetrics behaves
// the way it always has and includes every variant unconditionally.
export interface MetricCapabilities {
    supportsTotal: (metricId: string) => boolean
}

export function getWidgetMetrics(
    widget: Widget | ModifiedWidget,
    capabilities?: MetricCapabilities
): string[] {
    const {
        metrics,
        data_source,
        include_change_metrics = false,
        type,
        show_totals = false,
        group_by,
    } = widget
    const dataSourceConfig = DATA_SOURCES[data_source]
    const metricDependencies: Field<any>[] = []

    if (dataSourceConfig?.getMetricDependencies) {
        const groupByIds: string[] = group_by.map((group) => {
            return typeof group === 'string' ? group : (group?.id ?? '--')
        })
        const metricIds: string[] = metrics.map((metric) => {
            return typeof metric === 'string' ? metric : (metric?.id ?? '--')
        })

        groupByIds.forEach((id) => {
            const dependentMetrics =
                dataSourceConfig?.getMetricDependencies?.(widget)?.[id]
            dependentMetrics?.forEach((metric) => {
                if (!metricIds.includes(metric?.id)) {
                    metricDependencies.push(metric)
                }
            })
        })
    }

    const allRequiredMetrics = [...metrics, ...metricDependencies]

    const widgetMetrics: string[] = []

    allRequiredMetrics.forEach((metric) => {
        if (!metric) {
            return
        }

        const key = typeof metric === 'string' ? metric : metric.id
        widgetMetrics.push(key)

        const isSegmentDateMetric =
            data_source === 'segment_facts' &&
            ['report_date__min', 'report_date__max'].includes(key)

        const metricSupportsTotal = capabilities?.supportsTotal(key) ?? true

        if (type === TABLE && show_totals && metricSupportsTotal) {
            widgetMetrics.push(`${key}__total`)

            if (include_change_metrics && !isSegmentDateMetric) {
                widgetMetrics.push(`${key}__total__last_period`)
                widgetMetrics.push(`${key}__total__change`)
                widgetMetrics.push(`${key}__total__percentage_change`)
            }
        }

        if (
            include_change_metrics &&
            type !== TIME_SERIES &&
            !isSegmentDateMetric
        ) {
            widgetMetrics.push(`${key}__last_period`)
            widgetMetrics.push(`${key}__change`)
            widgetMetrics.push(`${key}__percentage_change`)
        }
    })

    return widgetMetrics
}

export function getWidgetGroupBys({
    group_by,
    data_source,
}: Widget | ModifiedWidget): string[] {
    const dataSourceConfig = DATA_SOURCES[data_source]
    const groupByDependencies: Field<any>[] = []

    if (dataSourceConfig?.groupByDependencies) {
        const groupByIds: string[] = group_by.map((group) => {
            return typeof group === 'string' ? group : (group?.id ?? '--')
        })

        groupByIds.forEach((id) => {
            const dependentGroupBys =
                dataSourceConfig?.groupByDependencies?.[id]
            dependentGroupBys?.forEach((groupBy) => {
                if (!groupByIds.includes(groupBy?.id)) {
                    groupByDependencies.push(groupBy)
                }
            })
        })
    }

    return transformToString([...group_by, ...groupByDependencies])
}

export function removeUIStateFromWidget(widget: Widget): Widget {
    return produce(widget, (draft: any) => {
        Object.keys(defaultWidgetState).forEach((key) => {
            delete draft[key]
        })
        if (!isEmpty(draft?.filters?.[DATES])) {
            draft.filters[DATES] = deserializeDatesFilter(draft.filters[DATES])
        }

        // Never saved: a dashboard's lag is worked out from its sources when it
        // renders (features/Dashboards/rangeLag), and a saved copy goes stale.
        if (draft?.filters) {
            delete draft.filters.rangeLag
        }

        if (widget.layout.y === Infinity) {
            draft.layout.y = Y_BOTTOM_OF_PAGE
        }
    })
}

export function removeUIStateFromTabsAndWidgets(
    tabs: DashboardTab[]
): DashboardTab[] {
    return tabs.map((tab) => ({
        ...produce(tab, (draft: any) => {
            Object.keys(defaultDashboardTabState).forEach((key) => {
                delete draft[key]
            })

            if (!isEmpty(draft?.localFilters?.[DATES])) {
                draft.localFilters[DATES] = deserializeDatesFilter(
                    draft.localFilters[DATES]
                )
            }

            if (draft?.localFilters) {
                delete draft.localFilters.rangeLag
            }
        }),
        widgets: tab.widgets.map(removeUIStateFromWidget),
    }))
}

export function getNewYPosition(widgets: Widget[]): number {
    const max = widgets
        .map((w) => w.layout)
        .reduce((prev, current) => (prev.y > current.y ? prev : current))

    return max.y + max.h
}
